import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { getColumnWidths, ExercisesTableRow } from "./ExercisesTableRow";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  BodypartType,
  MovementPatternType,
  ExerciseAssetDto,
  ExerciseAssetStatus,
} from "@growth-machine-llc/stridist-api-client";
import { EditorProgramContext } from "../new-editor/hooks";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import { ExerciseLibraryDrawer } from "./ExerciseLibraryDrawer";
import ExerciseLibraryDrawerContext from "../../contexts/ExericeLibraryDrawerContext";
import {
  ALL_OPTIONS,
  useExerciseListFilterContext,
} from "../../contexts/ExerciseLibraryFilterContext";
import { useDebounce } from "../../hooks/useDebounce";
import { BodypartFilter } from "./filters/BodypartFilter";
import { MovementPatternFilter } from "./filters/MovementPatternFilter";
import { NameSortButton } from "./filters/NameSortButton";
import { ExercisesTableMobileView } from "./ExercisesTableCardsView";
import {
  ExerciseLibraryCardSkeleton,
  ExerciseLibraryTableSkeleton,
} from "../loading/ExerciseLibrarySkeletons";
import clsx from "clsx";
import { CategoryType } from "./ManageLibraryExercise";
import ExerciseTagsService from "../../services/ExerciseTagsService";
import { TagsFilters, NONE_TAG_ID } from "./filters/TagsFilters";
import { JourneyAnchors, mapLibraryPrivacyOptions } from "../../constants";
import { Info } from "lucide-react";
import MinimizedTooltip from "../tooltip/MinimizedTooltip";
import { CoachLibraryTableScreen } from "../library/CoachLibraryTableScreen";

const useStyles = makeStyles((theme) => ({
  tabs: {
    "& span": {
      fontWeight: "bold",
      fontSize: 14,
      textTransform: "uppercase",
    },
    borderBottom: "1px solid",
    borderBottomColor: theme.palette.selected.main,
  },

  container: {
    margin: 0,
    containerType: "inline-size",

    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(1, 2),
    },
  },

  nameSortButton: {
    padding: 0,
  },

  infoIcon: {
    marginLeft: theme.spacing(1.2),
  },

  suggestedBy: {
    fontWeight: "bold",
    fontSize: 15,
    color: theme.palette.text.primary,
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

export interface ExercisesTableProps {
  status: ExerciseAssetStatus;
}

export const getBodypartTypes = (filters: Record<BodypartType, boolean>) =>
  Object.entries(filters)
    .filter(([, enabled]) => enabled)
    .map(([type]) => type) as BodypartType[];

export const getMovementPatternTypes = (
  filters: Record<MovementPatternType, boolean>,
) => {
  return Object.entries(filters)
    .filter(([, enabled]) => enabled)
    .map(([type]) => type) as MovementPatternType[];
};

export const areAllSelected = <T extends CategoryType>(
  selected: T[],
  allOptions: T[],
) => {
  return (
    selected?.length === allOptions?.length &&
    allOptions.every((item) => selected.includes(item))
  );
};

export const allBodyparts = Object.values(BodypartType);
export const allMovementPatterns = Object.values(MovementPatternType);

export const allBodypartsSelected = (selected: BodypartType[]) =>
  areAllSelected(selected, allBodyparts);

export const allMovementPatternsSelected = (selected: MovementPatternType[]) =>
  areAllSelected(selected, allMovementPatterns);

export const COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY =
  "exercise-library-assets";

export const ALL_EXERCISE_TAGS_QUERY_KEY = "all-exercise-tags";

//TODO_STR-2139: Set rows per page based on screen size

export function ExercisesTable(props: ExercisesTableProps) {
  const { status } = props;
  const s = useStyles();

  const isPendingTab = status === ExerciseAssetStatus.PENDING;
  const isPublishedTab = status === ExerciseAssetStatus.PUBLISHED;
  const isArchivedTab = status === ExerciseAssetStatus.ARCHIVED;

  const disableChangeExercise =
    status === ExerciseAssetStatus.ARCHIVED ||
    status === ExerciseAssetStatus.PENDING;

  const columnWidths = getColumnWidths(isPendingTab, isPublishedTab);

  const { breakpoints } = useTheme();
  const mdUp = useMediaQuery(breakpoints.up("md"));

  const createButtonContext = React.useContext(ExerciseLibraryDrawerContext);
  const {
    query,
    sortKey,
    bodyparts: contextBodyparts,
    movementPatterns: localMovementPatterns,
    privacy,
    tagIds: localStorageTagIds,
    page,
    rowsPerPage,
    handleBodypartChange,
    handleMovementPatternChange,
    handleSortKeyChange,
    handleTagIdsChange,
    handlePageChange,
    handleRowsPerPageChange,
  } = useExerciseListFilterContext();

  const { data: exerciseTags } = useQuery({
    queryKey: [ALL_EXERCISE_TAGS_QUERY_KEY],
    queryFn: () => {
      return ExerciseTagsService.getAllExerciseTags();
    },
  });

  const debounceQuery = useDebounce(query, 500);

  const { exerciseLibraryDrawerOpen, setExerciseLibraryDrawerOpen } =
    createButtonContext;

  const [exerciseAsset, setExerciseAsset] =
    React.useState<ExerciseAssetDto | null>(null);
  // state to hold public/team copy of exercise to sync with
  const [exerciseToSyncWith, setExerciseToSyncWith] =
    React.useState<ExerciseAssetDto | null>(null);

  const isAllBodypartsSelected = contextBodyparts === ALL_OPTIONS;

  const isAllMovementPatternsSelected = localMovementPatterns === ALL_OPTIONS;

  const bodyparts = isAllBodypartsSelected ? allBodyparts : contextBodyparts;

  const movementPatterns = isAllMovementPatternsSelected
    ? allMovementPatterns
    : localMovementPatterns;

  const isAllTagsSelected = localStorageTagIds === ALL_OPTIONS;

  const allTagIds = (exerciseTags ?? []).map((tag) => tag.id);

  const tagIds = isAllTagsSelected
    ? [...allTagIds, NONE_TAG_ID]
    : localStorageTagIds;

  const { data: exerciseAssetItems, isFetching } = useQuery({
    queryKey: [
      COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
      {
        page,
        rowsPerPage,
        query: debounceQuery,
        sortKey,
        bodyparts,
        movementPatterns,
        status,
        privacy,
        tagIds: isAllTagsSelected ? null : tagIds,
      },
    ],
    queryFn: () =>
      ExerciseAssetsService.getExerciseAssets(
        page,
        rowsPerPage,
        query,
        status,
        isAllBodypartsSelected ? null : bodyparts,
        isAllMovementPatternsSelected ? null : movementPatterns,
        sortKey,
        mapLibraryPrivacyOptions(privacy),
        isAllTagsSelected ? null : tagIds,
      ),
    placeholderData: keepPreviousData,
  });

  const handleSelectExercise = (
    exerciseAsset: ExerciseAssetDto,
    publicTeamExercise?: ExerciseAssetDto,
  ) => {
    setExerciseAsset(exerciseAsset);
    publicTeamExercise && setExerciseToSyncWith(publicTeamExercise);
    setExerciseLibraryDrawerOpen(true);
  };

  const handleCloseExerciseDrawer = () => {
    setExerciseLibraryDrawerOpen(false);
    setExerciseAsset(null);
    setExerciseToSyncWith(null);
  };

  const emptyRows = Array.from(new Array(rowsPerPage));

  React.useEffect(() => {
    if (
      exerciseTags &&
      exerciseTags.length > 0 &&
      (!tagIds || tagIds.length === 0)
    ) {
      handleTagIdsChange(null, true);
    }
  }, [exerciseTags, tagIds, handleTagIdsChange]);

  const renderTableHead = React.useCallback(
    () =>
      mdUp && (
        <TableHead className={JourneyAnchors.EXERCISES_TABLE_HEADER}>
          <TableRow>
            <TableCell width={columnWidths.thumbnail}>Video</TableCell>
            <TableCell width={columnWidths.title}>
              <NameSortButton
                sortKey={sortKey}
                className={s.nameSortButton}
                onChangeSortKey={handleSortKeyChange}
              />
            </TableCell>
            {!isPendingTab && (
              <>
                <TableCell align="center" width={columnWidths.movementPattern}>
                  <MovementPatternFilter
                    onChange={handleMovementPatternChange}
                    contextFilters={movementPatterns}
                  />
                </TableCell>
                <TableCell align="center" width={columnWidths.bodypart}>
                  <BodypartFilter
                    onChange={handleBodypartChange}
                    contextFilters={bodyparts}
                  />
                </TableCell>
              </>
            )}
            {!isPendingTab ? (
              <TableCell align="center" width={columnWidths.tags}>
                {exerciseTags?.length > 0 && tagIds?.length > 0 ? (
                  <TagsFilters
                    selectedTagIds={tagIds}
                    onTagChange={handleTagIdsChange}
                    tags={exerciseTags}
                  />
                ) : (
                  "Tags"
                )}
              </TableCell>
            ) : (
              <TableCell width={columnWidths.suggestedBy}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <Button variant="text" className={s.suggestedBy}>
                    Suggested by
                  </Button>
                  <MinimizedTooltip
                    opacity={1}
                    placement="top"
                    title="Client who suggested this exercise"
                  >
                    <Info className={s.infoIcon} />
                  </MinimizedTooltip>
                </Box>
              </TableCell>
            )}
            {isArchivedTab && (
              <TableCell align="center" width={columnWidths.images}>
                Media
              </TableCell>
            )}

            {isPublishedTab && (
              <TableCell align="center" width={columnWidths.type}>
                Access
              </TableCell>
            )}
            <TableCell align="center" width={columnWidths.actions}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
      ),
    [
      mdUp,
      columnWidths,
      sortKey,
      s.nameSortButton,
      handleSortKeyChange,
      handleMovementPatternChange,
      movementPatterns,
      handleBodypartChange,
      bodyparts,
      exerciseTags,
      tagIds,
      handleTagIdsChange,
      isPendingTab,
      isArchivedTab,
      isPublishedTab,
      s.infoIcon,
      s.suggestedBy,
    ],
  );

  const renderMobileFilters = React.useCallback(
    () =>
      !isPendingTab &&
      !mdUp && (
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
          flexWrap={"wrap"}
        >
          <MovementPatternFilter
            onChange={handleMovementPatternChange}
            contextFilters={movementPatterns}
          />
          <BodypartFilter
            onChange={handleBodypartChange}
            contextFilters={bodyparts}
          />
          {exerciseTags && tagIds ? (
            <TagsFilters
              selectedTagIds={tagIds}
              onTagChange={handleTagIdsChange}
              tags={exerciseTags}
            />
          ) : (
            "Tags"
          )}
        </Box>
      ),
    [
      isPendingTab,
      mdUp,
      handleMovementPatternChange,
      movementPatterns,
      handleBodypartChange,
      bodyparts,
      exerciseTags,
      tagIds,
      handleTagIdsChange,
    ],
  );

  const renderRow = React.useCallback(
    (exercise: ExerciseAssetDto, index: number) =>
      mdUp && (
        <ExercisesTableRow
          exerciseInfo={exercise}
          className={index === 0 ? JourneyAnchors.FIRST_EXERCISE : ""}
          onSelectedExercise={handleSelectExercise}
          isPendingTab={isPendingTab}
          isPublishedTab={isPublishedTab}
          isArchivedTab={isArchivedTab}
        />
      ),
    [mdUp, isPendingTab, isPublishedTab, isArchivedTab, handleSelectExercise],
  );

  const renderMobileCard = React.useCallback(
    (exercise: ExerciseAssetDto) =>
      !mdUp && (
        <ExercisesTableMobileView
          exerciseInfo={exercise}
          onSelectedExercise={handleSelectExercise}
        />
      ),
    [mdUp, handleSelectExercise],
  );

  const renderSkeletonTable = React.useCallback(
    (className?: string) =>
      mdUp && (
        <>
          {emptyRows.map((_, index) => (
            <ExerciseLibraryTableSkeleton
              key={index}
              className={className}
              isPendingTab={isPendingTab}
              isPublishedTab={isPublishedTab}
              isArchivedTab={isArchivedTab}
            />
          ))}
        </>
      ),
    [mdUp, emptyRows, isPendingTab, isPublishedTab, isArchivedTab],
  );

  const renderSkeletonCards = React.useCallback(
    () =>
      !mdUp && (
        <>
          {emptyRows.map((_, index) => (
            <ExerciseLibraryCardSkeleton key={index} />
          ))}
        </>
      ),
    [mdUp, emptyRows],
  );

  return (
    <>
      <CoachLibraryTableScreen<ExerciseAssetDto>
        mode={mdUp ? "desktop" : "mobile"}
        data={exerciseAssetItems?.items}
        isLoading={isFetching}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        page={page - 1}
        onPageChange={(newPage) => handlePageChange(newPage + 1)}
        totalCount={exerciseAssetItems?.totalCount || 0}
        renderTableHead={renderTableHead}
        renderRow={renderRow}
        renderMobileCard={renderMobileCard}
        renderSkeletonTable={renderSkeletonTable}
        renderSkeletonCards={renderSkeletonCards}
        renderMobileFilters={renderMobileFilters}
        emptyMessage={
          isPendingTab
            ? "Only clients can add pending exercises. Don't worry, once they propose a new exercise to your library, we will notify you and you will see them here."
            : "No exercises found for current filters."
        }
        colCount={9}
      />
      <EditorProgramContext.Provider value={{ programId: 0 }}>
        {exerciseLibraryDrawerOpen && (
          <ExerciseLibraryDrawer
            exerciseAsset={exerciseAsset}
            onUpdate={() => {
              setExerciseLibraryDrawerOpen(false);
            }}
            onSelectExercise={(exercise, exerciseToSyncWith) => {
              setExerciseAsset(exercise);
              setExerciseToSyncWith(exerciseToSyncWith);
            }}
            exerciseToSyncWith={exerciseToSyncWith}
            onCancel={handleCloseExerciseDrawer}
            onClose={handleCloseExerciseDrawer}
            onRemove={handleCloseExerciseDrawer}
            open={exerciseLibraryDrawerOpen}
            disableChangeExercise={disableChangeExercise}
          />
        )}
      </EditorProgramContext.Provider>
    </>
  );
}
